import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  TextField,
  Label,
  Button,
  Avatar,
  Checkbox,
  Icon,
  Utils,
  PortalWrapper,
} from 'mw-style-react';
import cn from 'classnames';
import useOutsideClick from 'useOutsideClick';
import AppUtils from '@control-front-end/utils/utils';
import { RECENT } from 'constants';
import useIntl from 'useIntl';
import mes from './intl';
import './AccessRules.scss';

function AccessRules({
  rules,
  ownerId,
  accountOwnerId,
  onChange,
  filterTypes,
  allPrivs,
  styleType = 'modal',
  disableOwner = true,
}) {
  const t = useIntl();
  const dispatch = useDispatch();
  const rightsMenuRef = useRef();
  let users = useSelector((state) => state.users);
  if (filterTypes) users = users.filter((i) => filterTypes.includes(i.type));
  const [filter, setFilter] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [userMenu, toggleUserMenu] = useState(null);

  useOutsideClick({ ref: rightsMenuRef, callback: () => toggleUserMenu(null) });

  /**
   * Загрузить рекомендации по пользователям
   */
  const loadSuggestions = () => {
    dispatch({
      type: RECENT.REQUEST,
      payload: {
        objTypes: 'user',
      },
      callback: (data) => {
        if (filterTypes)
          data = data.filter((i) => filterTypes.includes(i.type));
        setSuggestions(data);
      },
    });
  };

  useEffect(() => {
    loadSuggestions();
  }, []);

  const makeNewRule = ({ id, nick, value, avatar, pictureUrl }) => {
    const privs = {
      view: true,
      modify: id === accountOwnerId || id === ownerId,
      remove: id === accountOwnerId || id === ownerId,
    };
    if (allPrivs) {
      privs.sign = id === accountOwnerId;
      privs.execute = false;
    }
    return {
      action: 'create',
      avatar: avatar || pictureUrl,
      name: nick || value,
      userId: id,
      privs,
    };
  };

  /**
   * Добавление пользователя
   */
  const handleAddUser = (user) => {
    const filterRules = rules.filter(({ userId }) => user.id !== userId);
    onChange([makeNewRule(user), ...filterRules]);
    setFilter('');
    loadSuggestions();
  };

  /**
   * Удаление пользователя
   */
  const handleDelUser = (userId) => {
    if (userId === ownerId && disableOwner) return;
    const list = rules.slice();
    const index = list.findIndex((u) => u.userId === userId);
    if (index === -1) return;
    const rule = list[index];
    if (rule.action === 'create') {
      list.splice(index, 1);
    } else {
      list.splice(index, 1, { ...rule, action: 'delete' });
    }
    onChange(list);
    loadSuggestions();
  };

  /**
   * Изменение доступов для пользователя
   */
  const handleAccessChange = (userId, level, value) => {
    const list = structuredClone(rules);
    const index = list.findIndex((u) => u.userId === userId);
    if (index === -1) return;
    const rule = list[index];
    const privs = rule.privs || {};
    privs[level] = value;
    if (rule.action !== 'create') rule.action = 'update';
    const item = { ...rule, privs };
    list.splice(index, 1, item);
    onChange(list);
  };

  /**
   * Фильтр пользователей по имени
   */
  const filterByName = (list) => {
    const inputValue = AppUtils.transliterate(filter).toLowerCase().trim();
    return list.filter((item) => {
      const itemValue = AppUtils.transliterate(item.value).toLowerCase();
      const itemEmail = item.email.toLowerCase();
      return (
        itemValue.indexOf(inputValue) !== -1 ||
        itemEmail.indexOf(inputValue) !== -1
      );
    });
  };

  /**
   * Отрисовать найденных пользователей
   */
  const renderSearchResults = () => {
    const availableUsers = users.filter(
      (user) =>
        !rules.find((r) => r.userId === user.id && r.action !== 'delete')
    );
    const suggestedUsers = suggestions.filter(
      (user) =>
        !rules.find((r) => r.userId === user.id && r.action !== 'delete')
    );
    const restUsers = availableUsers.filter(
      (user) => !suggestedUsers.find((u) => u.id === user.id)
    );
    const resultList = filter.length
      ? filterByName(availableUsers)
      : suggestedUsers.concat(restUsers);
    if (!resultList.length) {
      return (
        <div styleName="userAccess__search__result__content">
          <div styleName="userAccess__noMatches">
            {filter.length ? (
              <Label
                styleName="userAccess__noMatches__title"
                fontWeight="semibold"
                value={t(mes.noMatches)}
              />
            ) : null}
          </div>
        </div>
      );
    }
    const title = filter.length
      ? `${t(mes.searchResults)}: ${resultList.length}`
      : `${t(mes.suggestions)}:`;
    const items = resultList.map((user) => (
      <div
        key={user.id}
        styleName={cn('userAccess__item', 'search')}
        onClick={() => handleAddUser(user)}
      >
        <div styleName="userAccess__avatar__box">
          <Avatar
            styleName="userAccess__avatar"
            size="medium"
            src={user.avatar || user.pictureUrl}
            label={user.nick || user.value}
          />
        </div>
        <div styleName="userAccess__label">
          <Label value={user.nick || user.value} />
        </div>
      </div>
    ));

    return (
      <div styleName="userAccess__search__result">
        <div styleName="userAccess__search__result__header">
          <Label value={title} fontSize="semibold" />
        </div>
        <div styleName="userAccess__search__result__content">{items}</div>
      </div>
    );
  };

  /**
   * Чекбоксы доступных уровней доступа
   */
  const renderCheckboxes = (userId, privs, visibility) => {
    // Корректировка для случаев, где галочка была снята (CE-2388)
    const getVisibility = (priv) => (priv ? visibility : 'visible');
    return (
      <>
        <Checkbox
          value={privs.view}
          visibility={getVisibility(privs.view)}
          onChange={({ value }) => handleAccessChange(userId, 'view', value)}
        >
          <Label value={t(mes.view)} />
        </Checkbox>
        <Checkbox
          value={privs.modify}
          visibility={getVisibility(privs.modify)}
          onChange={({ value }) => handleAccessChange(userId, 'modify', value)}
        >
          <Label value={t(mes.edit)} />
        </Checkbox>
        <Checkbox
          value={privs.remove}
          visibility={getVisibility(privs.remove)}
          onChange={({ value }) => handleAccessChange(userId, 'remove', value)}
        >
          <Label value={t(mes.remove)} />
        </Checkbox>
        {allPrivs ? (
          <Checkbox
            value={privs.sign}
            onChange={({ value }) => handleAccessChange(userId, 'sign', value)}
          >
            <Label value={t(mes.sign)} />
          </Checkbox>
        ) : null}
        {allPrivs ? (
          <Checkbox
            value={privs.execute}
            onChange={({ value }) =>
              handleAccessChange(userId, 'execute', value)
            }
          >
            <Label value={t(mes.execute)} />
          </Checkbox>
        ) : null}
      </>
    );
  };

  /**
   * Выбор прав доступа в виде выпадающего меню
   */
  const userAccessSelect = (userId, privs, visibility) => {
    const activeRights = Object.keys(privs).filter((key) => !!privs[key]);
    const accessString =
      activeRights.length === 1
        ? Utils.toUpperLatter(activeRights[0])
        : `${activeRights.length} rights`;
    return (
      <div styleName="userAccess__select">
        <TextField
          id={`privs_${userId}`}
          styleName="userAccess__select__field"
          bordered={true}
          readOnly={true}
          rightIcon="dropdown"
          value={accessString}
          onFocus={() => toggleUserMenu(userId)}
        />
        {userMenu === userId ? (
          <PortalWrapper
            root={document.getElementById('mainRoot')}
            node={document.getElementById(`privs_${userId}`)}
            width={120}
            vAlign="bottom"
            hAlign="left"
            offsetY={36}
          >
            <div ref={rightsMenuRef} styleName="userAccess__select__menu">
              {renderCheckboxes(userId, privs, visibility)}
              <Button
                styleName="userAccess__select__btn"
                size="small"
                fontWeight="normal"
                label={t(mes.done)}
                onClick={() => toggleUserMenu(null)}
              />
            </div>
          </PortalWrapper>
        ) : null}
      </div>
    );
  };

  const renderSelectedUser = () => {
    const filteredRules = rules.filter(
      ({ userType, action }) => action !== 'delete' && userType !== 'system'
    );
    const items = filteredRules.map(({ userId, name, avatar, privs }) => {
      const visibility =
        (userId === ownerId && disableOwner) || userId === accountOwnerId
          ? 'disabled'
          : 'visible';
      const label = userId === ownerId ? `${name} (owner)` : name;
      return (
        <div key={userId} styleName={cn('userAccess__item', 'selected')}>
          <div styleName="userAccess__item__info">
            <div styleName="userAccess__avatar__box">
              <Avatar
                styleName="userAccess__avatar"
                size="medium"
                src={avatar}
                label={name}
              />
            </div>
            <div styleName="userAccess__label">
              <Label value={label} />
            </div>
          </div>
          {userAccessSelect(userId, privs, visibility)}
          <div styleName={cn('userAccess__controls', { allPrivs })}>
            {renderCheckboxes(userId, privs, visibility)}
            <div
              styleName="userAccess__controls__close"
              onClick={() => handleDelUser(userId)}
            >
              <Icon type="close" visibility={visibility} />
            </div>
          </div>
        </div>
      );
    });

    return (
      <div styleName={cn('userAccess__search__result', 'selected')}>
        <div styleName="userAccess__search__result__header">
          <Label
            value={`${t(mes.selectedTitle)}: ${filteredRules.length}`}
            fontSize="semibold"
          />
          <div styleName="userAccess__controls__labels compact">
            <Label value={t(mes.accessRights)} />
          </div>
          <div
            styleName={cn('userAccess__controls__labels full', { allPrivs })}
          >
            <Label value={t(mes.view)} />
            <Label value={t(mes.edit)} />
            <Label value={t(mes.remove)} />
            {allPrivs ? <Label value={t(mes.sign)} /> : null}
            {allPrivs ? <Label value={t(mes.execute)} /> : null}
          </div>
        </div>
        <div styleName="userAccess__search__result__content">{items}</div>
      </div>
    );
  };

  return (
    <div styleName={cn('userAccess', styleType)}>
      <div styleName="userAccess__search">
        <TextField
          styleName="userAccess__search__field"
          leftIcon="search"
          size="large"
          bordered="true"
          value={filter}
          rightIcon={filter.length ? 'close' : ''}
          onClickRightIcon={() => setFilter('')}
          placeholder={t(mes.searchUserPlaceholder)}
          onChange={({ value }) => setFilter(value)}
        />
      </div>
      {renderSearchResults()}
      {renderSelectedUser()}
    </div>
  );
}

AccessRules.propTypes = {
  styleType: PropTypes.oneOf(['modal', 'embeded']),
  disableOwner: PropTypes.bool,
  rules: PropTypes.array,
  allPrivs: PropTypes.bool,
  filterTypes: PropTypes.array,
  ownerId: PropTypes.number,
  onChange: PropTypes.func,
};

export default AccessRules;
